/**
 * Route registry — single source of truth for all routes and their metadata.
 *
 * Each route defines:
 *   path    — URL pattern (NavGo-compatible)
 *   load    — lazy import function
 *   meta    — route metadata (navigationType, transition, scroll, auth, prefetch...)
 *
 * `meta.prefetch(params)` returns the resource(s) to warm the query cache with
 * on explicit link intent. Return a single resource, array, or void.
 */

import {
  coursesResource,
  coursesForYearResource,
  booksResource,
  booksForYearResource,
  libraryResource,
  courseResource,
  bookResource,
  publicCoursesResource,
  publicBooksResource,
  publicCourseResource,
  publicBookResource,
  lectureResource,
  readingModulesResource,
  bookmarksResource,
  playlistsResource,
  readingProgressListResource,
  dashboardResource,
  activityCalendarResource,
  activeSessionResource,
  myProgressResource,
} from '../data/resources.js'
import { session, sessionReady } from '../../store.svelte.js'

/**
 * Catalog routes serve two audiences off one URL — the same component either
 * way, reading the public endpoints when there is no session. The wait matters
 * on a hard load: the router reaches the route before `init_app` has answered,
 * and the page reads the session once at setup to decide which resources to
 * open, so it must not run before that settles.
 */
const catalogPage = (load) => async () => {
  await sessionReady()
  return load()
}

/**
 * Root serves two completely different experiences. Wait for auth before
 * choosing the chunk so an existing session never downloads Landing just to
 * replace it with `/g/{year}` a moment later.
 */
const rootPage = async () => {
  await sessionReady()
  return session.is_authenticated
    ? import('../../pages/AppRedirect.svelte')
    : import('../../pages/Landing.svelte')
}

/** @type {import('./types.js').RouteConfig[]} */
export const routeConfig = [
  // App redirect
  {
    path: '/app',
    load: () => import('../../pages/AppRedirect.svelte'),
    meta: {
      navigationType: 'replace',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
    },
  },

  {
    path: '/',
    load: rootPage,
    meta: {
      title: 'Landing',
      edges: [{ to: '/courses', semantic: 'push' }],
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: false,
    },
  },

  {
    // A real route, not just the gate's fallback screen. Signing in while the
    // URL was /login used to reload onto a path the router did not know, which
    // rendered the 404 page at the end of the funnel.
    path: '/login',
    load: () => import('../../pages/Login.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: false,
    },
  },

  // ── Auth Callback ──
  {
    path: '/auth-callback',
    load: () => import('../../pages/AuthCallback.svelte'),
    meta: {
      navigationType: 'replace',
      transition: 'fade',
      scroll: 'reset',
      auth: false,
    },
  },

  // ── Grade-scoped Routes (top-tier learning view) ──
  {
    path: '/g/:year',
    load: () => import('../../pages/GradePage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
      prefetch: (p) => {
        if (!p?.year) return
        const year = Number(p.year)
        return [
          readingModulesResource({ year }),
          coursesForYearResource(year),
          booksForYearResource(year),
          playlistsResource,
          readingProgressListResource,
          dashboardResource,
          activityCalendarResource(365),
          activeSessionResource,
        ]
      },
    },
  },
  {
    path: '/g/:year/courses',
    load: () => import('../../pages/Courses.svelte'),
    meta: {
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: (p) => p?.year && coursesForYearResource(Number(p.year)),
    },
  },
  {
    path: '/g/:year/books',
    load: () => import('../../pages/Books.svelte'),
    meta: {
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: (p) => p?.year && booksForYearResource(Number(p.year)),
    },
  },
  {
    path: '/g/:year/questions',
    load: () => import('../../pages/QuestionsPage.svelte'),
    meta: {
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: (p) => p?.year && readingModulesResource({ year: Number(p.year) }),
    },
  },
  {
    // The module/submodule/lecture browse pages are gone: learners reach
    // questions through practice, bookmarks or search, never by walking the
    // content tree. Only the reader routes below survive.
    path: '/g/:year/lectures/:lectureId/questions/:questionId',
    load: () => import('../../pages/QuestionReaderPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    // Lecture-free reader — submodule-attached questions, and any entry point
    // (bookmark, search hit, playlist item) that only knows the question id.
    path: '/g/:year/questions/:questionId',
    load: () => import('../../pages/QuestionReaderPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },

  // ── Public / User Routes ──
  {
    path: '/courses',
    load: catalogPage(() => import('../../pages/Courses.svelte')),
    meta: {
      title: 'Courses',
      edges: [
        { to: '/course/:id', semantic: 'push' },
        { to: '/course/:id/:slug', semantic: 'push' },
        { to: '/books', semantic: 'push' },
        { to: '/library', semantic: 'push' },
      ],
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: () => (session.is_authenticated ? coursesResource : publicCoursesResource),
    },
  },
  {
    path: '/course/:id',
    load: catalogPage(() => import('../../pages/CourseDetail.svelte')),
    meta: {
      title: 'Course Detail',
      edges: [
        { to: '/lecture/:id', semantic: 'push' },
        { to: '/subscribe', semantic: 'overlay' },
      ],
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: (p) => (p?.id ? (session.is_authenticated ? courseResource(p.id) : publicCourseResource(p.id)) : undefined),
    },
  },
  {
    // The canonical public URL carries the slug. Same page — the slug is not
    // read, the id is still what identifies the course.
    path: '/course/:id/:slug',
    load: catalogPage(() => import('../../pages/CourseDetail.svelte')),
    meta: {
      title: 'Course Detail',
      edges: [
        { to: '/lecture/:id', semantic: 'push' },
        { to: '/subscribe', semantic: 'overlay' },
      ],
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: (p) => (p?.id ? (session.is_authenticated ? courseResource(p.id) : publicCourseResource(p.id)) : undefined),
    },
  },
  {
    path: '/books',
    load: catalogPage(() => import('../../pages/Books.svelte')),
    meta: {
      title: 'Books',
      edges: [
        { to: '/book/:id', semantic: 'push' },
        { to: '/book/:id/:slug', semantic: 'push' },
      ],
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: () => (session.is_authenticated ? booksResource : publicBooksResource),
    },
  },
  {
    path: '/book/:id',
    load: catalogPage(() => import('../../pages/BookDetail.svelte')),
    meta: {
      title: 'Book Detail',
      edges: [
        { to: '/book/:id/read', semantic: 'push' },
        { to: '/subscribe', semantic: 'overlay' },
      ],
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: (p) => (p?.id ? (session.is_authenticated ? bookResource(p.id) : publicBookResource(p.id)) : undefined),
    },
  },
  {
    // Registered before /book/:id/:slug so the reader keeps the literal
    // segment. `catalog_slug` refuses to produce "read", so no book's
    // canonical URL can collide with this one.
    path: '/book/:id/read',
    load: () => import('../../pages/BookReader.svelte'),
    meta: {
      title: 'Book Reader',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    // The canonical public URL for a book. Must stay below the reader route.
    path: '/book/:id/:slug',
    load: catalogPage(() => import('../../pages/BookDetail.svelte')),
    meta: {
      title: 'Book Detail',
      edges: [
        { to: '/book/:id/read', semantic: 'push' },
        { to: '/subscribe', semantic: 'overlay' },
      ],
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: false,
      prefetch: (p) => (p?.id ? (session.is_authenticated ? bookResource(p.id) : publicBookResource(p.id)) : undefined),
    },
  },
  {
    path: '/library',
    load: () => import('../../pages/Library.svelte'),
    meta: {
      title: 'Library',
      edges: [
        { to: '/course/:id', semantic: 'push' },
        { to: '/course/:id/:slug', semantic: 'push' },
        { to: '/book/:id/read', semantic: 'push' },
      ],
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: () => [libraryResource, myProgressResource],
    },
  },
  {
    path: '/lecture/:id',
    load: () => import('../../pages/LectureView.svelte'),
    meta: {
      title: 'Lecture View',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: (p) => p?.id && lectureResource(p.id),
    },
  },
  {
    path: '/profile',
    load: () => import('../../pages/Profile.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/subscribe',
    load: () => import('../../pages/Subscribe.svelte'),
    meta: {
      title: 'Subscribe',
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    // Offer pages an ad links to; see src/lib/offers.js.
    path: '/offers/:slug',
    load: () => import('../../pages/OffersPage.svelte'),
    meta: {
      title: 'Offers',
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: false,
    },
  },
  {
    path: '/terms',
    load: () => import('../../pages/TermsOfSale.svelte'),
    meta: {
      title: 'Terms of sale',
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: false,
    },
  },
  {
    path: '/payment/return',
    load: () => import('../../pages/PaymentReturn.svelte'),
    meta: {
      title: 'Payment',
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
    },
  },

  // ── Reading / Learning (TTA v2) ──
  // Legacy unscoped twins of the reader routes; App.svelte rewrites them to
  // `/g/:year/…` once the year is known.
  {
    path: '/reading/lectures/:lectureId/questions/:questionId',
    load: () => import('../../pages/QuestionReaderPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    // Questions that hang off a submodule have no lecture to route through.
    path: '/reading/questions/:questionId',
    load: () => import('../../pages/QuestionReaderPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/reading/bookmarks',
    load: () => import('../../pages/BookmarksPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: () => bookmarksResource,
    },
  },
  {
    path: '/reading/explore',
    load: () => import('../../pages/SearchPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      prefetch: () => readingModulesResource(),
    },
  },

  // ── Practice / Assessment (TTA v3) ──
  {
    path: '/practice/sessions/:sessionId',
    load: () => import('../../pages/PracticeSessionPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/practice/sessions/:sessionId/summary',
    load: () => import('../../pages/PracticeSummaryPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/practice/sessions/:sessionId/review',
    load: () => import('../../pages/SessionReviewPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },

  // ── Student Dashboard (TTA v4) ──
  {
    path: '/dashboard',
    load: () => import('../../pages/DashboardPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },

  // ── Playlists & Collections (TTA v5) ──
  {
    path: '/playlists',
    load: () => import('../../pages/PlaylistsPage.svelte'),
    meta: {
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/playlists/:id',
    load: () => import('../../pages/PlaylistDetailsPage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },
  {
    path: '/collections/:slug',
    load: () => import('../../pages/CollectionBrowsePage.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
    },
  },

  // ── Admin Routes (superuser required) ──
  {
    path: '/admin',
    load: () => import('../../pages/Admin.svelte'),
    meta: {
      title: 'Admin Dashboard',
      edges: [
        { to: '/admin/users/', semantic: 'push' },
        { to: '/admin/courses/', semantic: 'push' },
        { to: '/admin/books/', semantic: 'push' },
      ],
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/landing/blocks/:blockId/edit',
    load: () => import('../../pages/admin/AdminLandingBlockEdit.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/landing/preview',
    load: () => import('../../pages/Landing.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'fade',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/landing/',
    load: () => import('../../pages/admin/AdminLanding.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/faculties/',
    load: () => import('../../pages/admin/Faculties.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/study-years/',
    load: () => import('../../pages/admin/StudyYears.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/users/',
    load: () => import('../../pages/admin/Users.svelte'),
    meta: {
      title: 'Admin Users',
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/courses/',
    load: () => import('../../pages/admin/AdminCourses.svelte'),
    meta: {
      title: 'Admin Courses',
      edges: [{ to: '/admin/courses/:course_id', semantic: 'push' }],
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/courses/:course_id',
    load: () => import('../../pages/admin/CourseDetails.svelte'),
    meta: {
      title: 'Admin Course Detail',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/books/',
    load: () => import('../../pages/admin/AdminBooks.svelte'),
    meta: {
      title: 'Admin Books',
      edges: [{ to: '/admin/books/:book_id', semantic: 'push' }],
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/books/:book_id',
    load: () => import('../../pages/admin/BookDetails.svelte'),
    meta: {
      title: 'Admin Book Detail',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/media/',
    load: () => import('../../lib/layouts/UploadsLayout.svelte'),
    meta: {
      title: 'Admin Media',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/subscriptions/',
    load: () => import('../../lib/layouts/SubscriptionLayout.svelte'),
    meta: {
      title: 'Admin Subscriptions',
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/learning',
    load: () => import('../../pages/admin/AnalyticsLearning.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/courses',
    load: () => import('../../pages/admin/AnalyticsCourses.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/courses/:course_id',
    load: () => import('../../pages/admin/AnalyticsCourseDetail.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/engagement',
    load: () => import('../../pages/admin/AnalyticsEngagement.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/cohorts',
    load: () => import('../../pages/admin/AnalyticsCohorts.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/content',
    load: () => import('../../pages/admin/AnalyticsContent.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/system',
    load: () => import('../../pages/admin/AdminSystem.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/videos',
    load: () => import('../../pages/admin/AnalyticsVideos.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/lectures',
    load: () => import('../../pages/admin/AnalyticsLectures.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/users',
    load: () => import('../../pages/admin/AnalyticsUsers.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/analytics/revenue',
    load: () => import('../../pages/admin/AnalyticsRevenue.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'reset',
      auth: true,
      superuser: true,
    },
  },

  // ── Question Reports Admin (TTA v5) ──
  {
    path: '/admin/reports',
    load: () => import('../../pages/admin/AdminQuestionReports.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },

  // ── Lectures Admin (TTA v6) ──
  {
    path: '/admin/lectures/',
    load: () => import('../../pages/admin/AdminLectures.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },

  // ── Quizzes Admin (TTA v1) ──
  {
    path: '/admin/academic-years/',
    load: () => import('../../pages/admin/AcademicYears.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/access/',
    load: () => import('../../pages/admin/AccessRules.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/bundles/',
    load: () => import('../../pages/admin/Bundles.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/modules/',
    load: () => import('../../pages/admin/Modules.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/submodules/',
    load: () => import('../../pages/admin/SubModules.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/translations/',
    load: () => import('../../pages/admin/Translations.svelte'),
    meta: {
      title: 'Translation Operations',
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/questions/create',
    load: () => import('../../pages/admin/AdminQuestionForm.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/questions/bulk-import',
    load: () => import('../../pages/admin/QuestionBulkImport.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/questions/:id/edit',
    load: () => import('../../pages/admin/AdminQuestionForm.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/questions/',
    load: () => import('../../pages/admin/Questions.svelte'),
    meta: {
      preserve: true,
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/question-illustrations/',
    load: () => import('../../pages/admin/QuestionIllustrations.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
  {
    path: '/admin/tags/',
    load: () => import('../../pages/admin/Tags.svelte'),
    meta: {
      navigationType: 'page',
      transition: 'push',
      scroll: 'restore',
      auth: true,
      superuser: true,
    },
  },
]
